import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { PartConfigurationTemplateView } from "src/api/generated/erp/db/types/tables/partConfigurationTemplateView.ts";
import { enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getPartRevisionStateLabels } from "src/api/generated/erp/db/types/enums/partRevisionState.ts";
import { SxProps, Theme } from "@mui/material/styles";
import { PartConfigurationTemplateEditor } from "src/components/views/erp/configurator/managing/salesPartConfigurationTemplates/PartConfigurationTemplateEditor.tsx";
import { PartConfigurationTemplatesApi } from "src/api/generated/erp/configurator/management/configurationTemplates/partConfigurationTemplatesApi.ts";
import { PartConfigurationTemplateEditApi } from "src/api/generated/erp/configurator/management/configurationTemplates/partConfigurationTemplateEditApi.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { AddConfigurationTemplateToSalesPartForm } from "src/components/views/erp/configurator/managing/salesPartConfigurationTemplates/AddConfigurationTemplateToSalesPartForm.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesPartConfigurationTemplatesDataGridProps {
	salesPartId: number;
	sx?: SxProps<Theme>;
}

export const SalesPartConfigurationTemplatesDataGrid = ({
	salesPartId,
}: SalesPartConfigurationTemplatesDataGridProps) => {
	return (
		<ClientSideDataGridModel
			fetchData={() =>
				PartConfigurationTemplatesApi.getSalesPartConfigurationTemplates({
					salesPartId,
				})
			}
			initialParams={{}}
			getRows={(response) => response}
			getRowId={(row) => row.partConfigurationTemplateId}
			gridId={"F8A1B2C3D4E5F6G7"}
			render={({ dataGridProps, refreshData }) => {
				return (
					<FormCrudDataGrid<PartConfigurationTemplateView>
						columns={[
							integerColumn({
								field: "revisionNumber",
								headerName: i18n.t("revision"),
								width: 100,
							}),
							enumColumn({
								field: "partRevisionState",
								headerName: i18n.t("revision_state"),
								enumLabels: getPartRevisionStateLabels(),
								width: 100,
							}),
							textColumn({
								field: "partRevisionNote",
								headerName: i18n.t("revision_note"),
								width: 200,
							}),
							integerColumn({
								field: "partConfigurationTemplateId",
								headerName: i18n.t("id"),
							}),
						]}
						disableMultipleRowSelection
						form={{
							addRowEnabled: true,
							editEnabled: true,
							dialogTitle: (row) =>
								row == null ? i18n.t("new_configuration_template") : i18n.t("configuration_template"),
							dialogSize: (row) => (row == null ? "sm" : "fullscreen"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.configurator.configurationTemplate.formDialog"),
								title: row == null ? i18n.t("new_configuration_template") : i18n.t("configuration_template"),
								data: {
									salesPartId,
									partConfigurationTemplateId: row?.partConfigurationTemplateId ?? null,
									partRevisionId: row?.partRevisionId ?? null,
								},
							}),
							component: ({ row, closeDialog }) =>
								row != null ?
									<PartConfigurationTemplateEditor
										partConfigurationTemplate={row}
										refreshTemplates={refreshData}
										closeDialog={closeDialog}
									/>
								:	<AddConfigurationTemplateToSalesPartForm
										salesPartId={salesPartId}
										closeDialog={closeDialog}
										refreshTemplatesDataGrid={refreshData}
									/>,
						}}
						remove={({ row }) =>
							PartConfigurationTemplateEditApi.delete_({
								partConfigurationTemplateId: row.partConfigurationTemplateId,
							})
						}
						agentContext={{
							key: defineAgentUiContextKey("erp.configurator.salesPartConfigurationTemplates.grid"),
							data: { salesPartId },
							rowFields: ["partConfigurationTemplateId", "salesPartId", "partRevisionId", "revisionNumber"],
							selectedRowPathSegment: (row) => `${i18n.t("revision")} ${row.revisionNumber}`,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
